import { Outlet, useLocation, useNavigate , Link, Navigate } from 'react-router-dom'
import { AdminBusinessContext } from '../../contexts/AdminBusinessContext'
import { useAuth } from '../../contexts/AuthContext'
import { useSocket } from '../../contexts/SocketContext'
import { useTheme } from '../../contexts/ThemeContext'
import { authAPI } from '../../services/api'
import toast from 'react-hot-toast'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import {
  HomeIcon,
  ClipboardDocumentListIcon,
  TableCellsIcon,
  QueueListIcon,
  BeakerIcon,
  ChartBarIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  BellSlashIcon,
  WifiIcon,
  UserGroupIcon,
  GiftIcon,
  ChatBubbleLeftRightIcon,
  ShoppingCartIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  DevicePhoneMobileIcon,
  ClockIcon,
  CreditCardIcon,
  TruckIcon
} from '@heroicons/react/24/outline'
import { t, LanguageSwitcher, getLanguage } from '../../i18n'
import { soundEnabled, setSoundEnabled, playOrderSound } from '../../utils/orderSound'
import { NAV_FEATURE } from '../../config/features'
import { servicesOf } from '../../config/services'
import { allowedPages } from '../../config/permissions'
import { useState  , useRef , useEffect} from 'react'
import { QRCodeCanvas } from 'qrcode.react'

// The admin menu is built from the services the store runs (menu & pre-orders,
// online store, bookings) and then narrowed to the pages this person may open.
// disabled: sections the platform owner switched off for this business.
// pages: the pages allowed for this person (null = no narrowing).
// storeMode: how the online store sells; an online-only store has no POS.
export function getNavigation(businessType, disabled = [], services = null, pages = null, storeMode = 'both') {
  const list = servicesOf(services, businessType)
  const has = id => list.includes(id)
  const sells = has('menu') || has('store')
  const counter = has('menu') || (has('store') && storeMode !== 'online')

  const nav = [{ name: 'Dashboard', href: '/admin/dashboard', icon: HomeIcon, page: 'dashboard' }]
  if (counter) nav.push({ name: 'POS', href: '/admin/pos', icon: ShoppingCartIcon, page: 'pos' })
  if (has('menu')) nav.push({ name: 'Menu', href: '/admin/menu', icon: ClipboardDocumentListIcon, page: 'menu' })
  if (has('store')) {
    nav.push({ name: 'Products', href: '/admin/products', icon: ClipboardDocumentListIcon, page: 'products' })
    nav.push({ name: 'Categories', href: '/admin/categories', icon: QueueListIcon, page: 'products' })
  }
  if (has('bookings')) nav.push({ name: 'Services', href: '/admin/services', icon: ClipboardDocumentListIcon, page: 'services' })
  if (has('menu')) {
    nav.push({ name: 'Reservations', href: '/admin/reservations', icon: ClockIcon, page: 'reservations' })
    nav.push({ name: 'Tables', href: '/admin/tables', icon: TableCellsIcon, page: 'tables' })
  }
  if (has('bookings') || has('menu')) nav.push({ name: 'Availability', href: '/admin/availability', icon: ClockIcon, page: 'availability' })
  if (sells) nav.push({ name: 'Orders', href: '/admin/orders', icon: QueueListIcon, page: 'orders' })
  if (has('bookings')) nav.push({ name: has('menu') ? 'Bookings' : 'Appointments & Reservations', href: '/admin/bookings', icon: QueueListIcon, page: 'bookings' })
  if (has('menu')) nav.push({ name: 'Kitchen', href: '/admin/kitchen', icon: BeakerIcon, page: 'kitchen' })
  if (sells) {
    nav.push({ name: 'Inventory', href: '/admin/inventory', icon: ChartBarIcon, page: 'inventory' })
    nav.push({ name: 'Shipping', href: '/admin/shipping', icon: TruckIcon, page: 'shipping' })
  }
  nav.push(
    { name: 'Employees', href: '/admin/employees', icon: UserGroupIcon, page: 'employees' },
    { name: 'Customers', href: '/admin/customers', icon: GiftIcon, page: 'customers' },
    { name: 'WhatsApp', href: '/admin/whatsapp', icon: ChatBubbleLeftRightIcon, page: 'whatsapp' },
  )
  if (sells) nav.push({ name: 'Reports', href: '/admin/reports', icon: ChartBarIcon, page: 'reports' })
  nav.push(
    { name: 'Website & Themes', href: '/admin/website', icon: DevicePhoneMobileIcon, page: 'website' },
    { name: 'Billing', href: '/admin/billing', icon: CreditCardIcon, page: 'billing' },
    { name: 'Settings', href: '/admin/settings', icon: CogIcon, page: 'settings' }
  )
  return nav.filter(item => !disabled.includes(NAV_FEATURE[item.href]) && (!pages || pages.includes(item.page)))
}

function AdminLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, forgetSession } = useAuth()
  const { isConnected } = useSocket()
  const [orderSound, setOrderSound] = useState(soundEnabled)
  const toggleOrderSound = () => {
    const next = !orderSound
    setSoundEnabled(next)
    setOrderSound(next)
    if (next) playOrderSound({ force: true })
  }
  const { getAppName, getSetting } = useTheme()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showQR, setShowQR] = useState(false)
const qrRef = useRef(null);
const hideTimeoutRef = useRef(null);
  const [isFullScreen, setIsFullScreen] = useState(document.fullscreenElement !== null)
  const [branchStatus, setBranchStatus] = useState({ loading: true, suspended: false, branchName: '' })
  const [businessType, setBusinessType] = useState(null)
  const [branch, setBranch] = useState(null)
  const [profileError, setProfileError] = useState(false)
  const [profileUser, setProfileUser] = useState(null)

  const loadedRef = useRef(false)
  // Each store's admin runs on its own subdomain; the main domain only hosts
  // the landing page and the platform console.
  const [movingToStore, setMovingToStore] = useState(false)
  const movingRef = useRef(false)
  const moveToStore = (websiteUrl) => {
    let target
    try { target = new URL(websiteUrl) } catch { return false }
    if (target.host === window.location.host || movingRef.current) return movingRef.current
    movingRef.current = true
    setMovingToStore(true)
    authAPI.handoff(location.pathname)
      .then(({ data }) => {
        if (!data.url) throw new Error('no store address')
        // The platform owner keeps the main-domain session for /platform.
        if (!user?.isSuperAdmin) forgetSession()
        // The store's address keeps the dashboard language.
        const url = new URL(data.url)
        url.hash = `${url.hash.slice(1)}${url.hash.length > 1 ? '&' : ''}lang=${getLanguage()}`
        window.location.replace(url.toString())
      })
      .catch(() => { movingRef.current = false; setMovingToStore(false) })
    return true
  }

  useEffect(() => {
    let current = true
    const refresh = () => authAPI.getProfile()
      .then(({ data }) => {
        if (!current) return
        if (!data.branch) throw new Error('No business assigned')
        if (!['restaurant', 'ecommerce', 'appointments', 'hotel', 'office'].includes(data.branch.business_type)) throw new Error('Invalid business type')
        if (data.branch.website_url && moveToStore(data.branch.website_url)) return
        loadedRef.current = true
        setProfileUser(data.user || null)
        setProfileError(false)
        setBranch(data.branch)
        setBranchStatus({
          loading: false,
          suspended: data.branch ? data.branch.is_active === false || data.branch.is_active === 0 : false,
          branchName: data.branch?.name || ''
        })
        setBusinessType(data.branch.business_type)
      })
      .catch(() => {
        // A hiccup while refreshing an already-open workspace: keep it as is.
        if (!current || loadedRef.current) return
        setProfileError(true)
        // No business to open: the platform owner goes to their console,
        // everyone else signs in again.
        if (user?.isSuperAdmin) return navigate('/platform', { replace: true })
        toast.error(t("We couldn't open your business. Please sign in again."), { id: 'profile-error' })
        logout()
        navigate('/admin/login', { replace: true })
      })
    refresh()
    window.addEventListener('focus', refresh)
    return () => { current = false; window.removeEventListener('focus', refresh) }
  }, [user?.id, location.pathname])

  // The pages this person may open: from their profile, else their role.
  const pages = allowedPages(profileUser || user)
  const filteredNavigation = getNavigation(businessType, branch?.disabled_features || [], branch?.services, pages, branch?.store_mode)
  // The plan picked at sign-up is paid from here, once the store is set up.
  const payPlan = branch?.plan && branch.billing && !branch.billing.subscribed && pages.includes('billing') && !location.pathname.startsWith('/admin/billing')

    // Production: the store's own subdomain. Dev (no APP_DOMAIN): ?branch=.
    const websitePath = branch?.website_url || `/?branch=${branch?.id || ''}`
    const qrUrl = branch?.website_url || `${window.location.origin}${websitePath}`
    const element = document.documentElement


useEffect(() => {
  const savedMode = localStorage.getItem("fullscreenMode");

  if (savedMode === "true") {
    element.requestFullscreen?.();
  }

  const onChange = () => updateFullscreenState();
  document.addEventListener("fullscreenchange", onChange);

  return () => {
    document.removeEventListener("fullscreenchange", onChange);
  };
}, [isFullScreen]);
    

const updateFullscreenState = () => {
  const active = document.fullscreenElement !== null;
  setIsFullScreen(active);
  localStorage.setItem("fullscreenMode", active ? "true" : "false");
};



const printQR = () => {
  const canvas = qrRef.current?.querySelector("canvas");

  if (!canvas) {
    alert(t("QR Code not found!"));
    return;
  }



  const imgData = canvas.toDataURL("image/png");

  const printWindow = window.open("", "PRINT", "height=600,width=600");

  printWindow.document.write(`
    <html>
    <head>
      <title>Print QR</title>
      <style>
        body { text-align: center; font-family: sans-serif; padding-top: 20px; }
        img { width: 260px; margin-top: 20px; }
        h3 { margin-top: 10px; }
      </style>
    </head>
    <body>
      <h3>Visit our website</h3>
      <img src="${imgData}" />
    </body>
    </html>
  `);

  printWindow.document.close();

  setTimeout(() => {
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  }, 400);
};

// When mouse enters → show instantly + cancel hiding
const handleMouseEnter = () => {
  if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
  setShowQR(true);
};

// When mouse leaves → wait 1s → then hide
const handleMouseLeave = () => {
  hideTimeoutRef.current = setTimeout(() => {
    setShowQR(false);
  }, 1000);
};


  const handleLogout = () => {
    logout()
    navigate('/admin/login')
  }

 const goFullscreen = () => {
    if (element.requestFullscreen) element.requestFullscreen()
    setIsFullScreen(true)
  }

  const exitFullscreen = () => {
    document.exitFullscreen()
    setIsFullScreen(false)
  }

  if (movingToStore) return <div className="p-12 text-center">{t("Opening your store…")}</div>
  if (profileError) return <div className="p-12 text-center">{t("Loading your workspace…")}</div>
  if (branchStatus.loading) return <div className="p-12 text-center">{t("Loading your workspace…")}</div>
  if (!branchStatus.suspended) {
    // Only pages in this person's menu open; anything else goes to their first page.
    const requested = location.pathname.replace(/\/$/, '')
    const allowed = filteredNavigation.some(item => item.href === requested || requested.startsWith(`${item.href}/`))
    if (!allowed) return <Navigate to={filteredNavigation[0]?.href || '/admin/login'} replace />
  }
  if (!branchStatus.loading && branchStatus.suspended && user?.role !== 'owner') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-red-100">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {branchStatus.branchName || t("This store")} {t("is suspended")}
          </h1>
          <p className="text-gray-500 mb-6">
            {t("Access has been paused by the platform owner. Your storefront is offline, and new orders can't be taken until this is resolved. Contact the platform owner to reactivate.")}
          </p>
          <button
            onClick={logout}
            className="w-full py-2.5 rounded-lg bg-gray-900 text-white font-medium hover:bg-gray-800"
          >
            {t("Sign out")}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 start-0 flex w-64 flex-col bg-white border-e border-gray-200">
          <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200">
            <div><h1 className="text-lg font-semibold text-gray-900">{t('{name} Admin', { name: branch?.name || '' })}</h1><p className="text-xs text-gray-500">{t(businessType === 'ecommerce' ? 'Ecommerce' : businessType === 'restaurant' ? 'Restaurant' : 'Appointments & Reservations')}</p></div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <span className="sr-only">{t("Close sidebar")}</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-1">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href
              return (
                <a
                  key={item.name}
                  href={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <item.icon className="me-3 h-5 w-5" />
                  {t(item.name)}
                </a>
              )
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex min-h-0 flex-col flex-grow bg-white border-e border-gray-200">
          <div className="flex h-16 shrink-0 items-center gap-3 px-4 border-b border-gray-200">
            {branch?.logo_url && <img src={branch.logo_url} alt="" className="h-9 w-9 rounded-lg object-contain bg-gray-50 border border-gray-100" />}
            <div><h1 className="text-lg font-semibold text-gray-900">{t('{name} Admin', { name: branch?.name || '' })}</h1><p className="text-xs text-gray-500">{t(businessType === 'ecommerce' ? 'Ecommerce' : businessType === 'restaurant' ? 'Restaurant' : 'Appointments & Reservations')}</p></div>
          </div>
          <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-1">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href
              return (
             <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  isActive
                    ? "bg-blue-100 text-blue-700 border border-blue-200"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <item.icon className="me-3 h-5 w-5" />
                {t(item.name)}
              </Link>

              )
            })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:ps-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">{t("Open sidebar")}</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>

            <div className="flex items-center gap-3 sm:gap-4">
              <button type="button" onClick={toggleOrderSound} aria-pressed={orderSound}
                title={orderSound ? t("New order sound is on") : t("New order sound is off")}
                aria-label={orderSound ? t("Turn off new order sound") : t("Turn on new order sound")}
                className={`rounded-lg p-1.5 ${orderSound ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-400 hover:bg-gray-100'}`}>
                {orderSound ? <BellIcon className="h-5 w-5" /> : <BellSlashIcon className="h-5 w-5" />}
              </button>
              {/* Connection status */}
              <div className="flex items-center text-sm">
                {isConnected ? (
                  <>
                    <WifiIcon className="h-4 w-4 text-green-500 me-1" />
                    <span className="text-green-600 hidden md:inline">{t("Connected")}</span>
                  </>
                ) : (
                  <>
                    <WifiIcon className="h-4 w-4 text-red-500 me-1" />
                    <span className="text-red-600 hidden md:inline">{t("Disconnected")}</span>
                  </>
                )}
              </div>
              <div className="hidden sm:flex items-center text-sm">
                {isFullScreen ? (
                  <>
                    <ArrowsPointingInIcon onClick={() => {
  if (!document.fullscreenElement) {
    element.requestFullscreen?.();
  } else {
    document.exitFullscreen?.();
  }
}}
 className="h-5 w-5 me-1 cursor-pointer" />
                  </>
                ) : (
                  <>
                    <ArrowsPointingOutIcon  onClick={() => {
  if (!document.fullscreenElement) {
    element.requestFullscreen?.();
  } else {
    document.exitFullscreen?.();
  }
}}
 className="h-5 w-5  me-1 cursor-pointer" />
                  </>
                )}
              </div>


 <div
  className="relative"
  onMouseEnter={handleMouseEnter}
  onMouseLeave={handleMouseLeave}
>
  <button
    onClick={() => window.open(websitePath, '_blank', 'noopener,noreferrer')}
    className="text-gray-700 hover:text-blue-600 flex items-center space-x-1"
  >
    <span className="text-sm hidden sm:inline">{t("View website")}</span>
    <DevicePhoneMobileIcon className="h-5 w-5" />
  </button>

  {/* Tooltip */}
  <div
    className={`
      absolute top-10 end-0 w-72 bg-white rounded-xl shadow-xl border
      z-50 p-4 text-center transition-all duration-500
      ${showQR
        ? "opacity-100 scale-100 blur-0"
        : "opacity-0 scale-95 blur-sm pointer-events-none"}
    `}
  >
    <p className="text-sm font-semibold text-gray-700 mb-2">
      {t("Scan to visit your website")}
    </p>

    <div ref={qrRef} className="flex justify-center mb-3">
      <QRCodeCanvas
        id="qrCanvas"
        value={qrUrl}
        size={160}
        includeMargin={true}
      />
    </div>

    <button
      onClick={printQR}
      className="w-full py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
    >
      {t("Print QR 🖨️")}
    </button>
  </div>
</div>


            

              {/* User menu */}
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600 hidden md:inline">
                  {user?.fullName || user?.username}
                </span>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded hidden sm:inline">
                  {t(user?.role)}
                </span>
                <LanguageSwitcher />
                <button
                  onClick={handleLogout}
                  className="text-gray-400 hover:text-gray-600"
                  title={t("Logout")}
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="px-4 sm:px-6 lg:px-8 py-8">
          {payPlan && (
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
              <p className="text-sm text-blue-900">
                <CreditCardIcon className="me-2 inline h-5 w-5 align-text-bottom" />
                {t('You chose the {plan} plan', { plan: t(branch.plan.name) })}
                {branch.billing.price && <> · <strong dir="ltr">{branch.billing.price.amount} {branch.billing.price.currency}</strong> / {t(branch.billing.interval === 'year' ? 'year' : 'month')}</>}
                {' '}{t('- pay for it to keep your store running.')}
              </p>
              <Link to="/admin/billing" className="btn-primary whitespace-nowrap">{t('Pay for my plan')}</Link>
            </div>
          )}
          <AdminBusinessContext.Provider value={{ branch, businessType, subscriptionLocked: Boolean(branch?.plan) && !['active', 'trialing'].includes(branch?.billing?.status) }}><Outlet /></AdminBusinessContext.Provider>
        </main>
      </div>
    </div>
  )
}

export default AdminLayout
